// REUSABLE: Form Wrapper Component
// Standard modal-based form layout with consistent styling. Used by: All forms (Timesheet entry, Task creation, etc.)
// Benefits: Consistent form UX across app, Built-in loading state handling, Standard Save/Cancel pattern, Follows existing modal patterns

import React from 'react';
import { Modal, Button } from 'react-bootstrap';

interface FormWrapperProps {
  title: string; // Form title shown in modal header
  show: boolean; // Whether the modal is visible
  children: React.ReactNode; // Form content (children)
  onSubmit: () => void; // Submit handler
  onCancel: () => void; // Cancel/Close handler
  loading?: boolean; // Loading state (disables buttons, shows spinner)
  submitLabel?: string; // Submit button label (default: "Save")
  cancelLabel?: string; // Cancel button label (default: "Cancel")
  size?: 'sm' | 'lg' | 'xl'; // Modal size (default: "lg")
  submitDisabled?: boolean; // Disable submit button (in addition to loading)
  customFooter?: React.ReactNode; // Optional footer content (overrides default buttons)
  showCloseButton?: boolean; // Show close button in header (default: true)
}

// Modal-based form wrapper with standard layout
export const FormWrapper: React.FC<FormWrapperProps> = ({
  title,
  show,
  children,
  onSubmit,
  onCancel,
  loading = false,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  size = 'lg',
  submitDisabled = false,
  customFooter,
  showCloseButton = true,
}) => {
  return (
    <Modal
      show={show}
      onHide={onCancel}
      backdrop="static"
      centered
      size={size}
      animation={false}
    >
      {/* Modal Header */}
      <div className="modal-header">
        <h2 className="fw-bolder">{title}</h2>
        {showCloseButton && (
          <div
            className="btn btn-icon btn-sm btn-active-icon-danger"
            style={{ cursor: 'pointer' }}
            onClick={onCancel}
          >
            <i className="fa-solid fa-xmark fs-1" />
          </div>
        )}
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        {children}
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        {customFooter ? (
          customFooter
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
              className="btn-sm"
            >
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={loading || submitDisabled}
              className="btn-sm"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default FormWrapper;


